import React, {useState} from "react";
import {DataGridPro} from "@mui/x-data-grid-pro";
import {
    Avatar,
    Backdrop,
    Box,
    Chip,
    CircularProgress,
    Tooltip,
    Typography
} from "@mui/material";
import {orange} from "@mui/material/colors";
import moment from "moment";
import {useTranslation} from "react-i18next";

import {MONTH_YEAR_FORMAT, YEAR_MONTH_FORMAT} from "../../config/DateTimeConstants";
import {
    AMENDMENT_DIRECTION,
    calculateNetAmendmentDurationInMinutes,
    calculateShiftDurationIncludingCheckInTimeInMinutes
} from "../../helpers/ShiftHelper";
import {formatDateAndTimeRange, formatMinutesAsHours} from "../../helpers/DateTimeRangeHelper";
import {retrieveServiceTypeIcon} from "../../helpers/ServiceTypeUtils";
import useShiftHistory from "../../hooks/useShiftHistory";
import {useTimezone} from "../../hooks/useTimezone";

import NoRowsOverlayLabel from "../common/NoRowsOverlayLabel";

import AmendmentDetailsDialog from "./AmendmentDetailsDialog";
import {PAGE_SIZE_OPTIONS} from "./MyHistorySessionsTable";
import MyHistoryShiftsSummaryTab from "./MyHistoryShiftsSummaryTab";

const SINGLE_LINE_ROW_HEIGHT_PIXELS = 40;
const MONTH_OPTIONS_COUNT = 12;

const EARLIEST_SHIFT_HISTORY_MONTH = process.env.REACT_APP_EARLIEST_SHIFT_HISTORY_MONTH;
const EARLIEST_MONTH_OPTION = moment(EARLIEST_SHIFT_HISTORY_MONTH, YEAR_MONTH_FORMAT);

const styles = {
    box: {
        serviceRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.2rem'
        },
        gridContainer: {
            minHeight: '12.5rem',
            minWidth: '18.75rem',
            width: '100%',
        },
        progressTrack: {
            width: '100%',
            height: '0.25rem',
        }
    },
    dataGrid: {
        "& .MuiDataGrid-cell": {
            alignItems: "center",
            backgroundColor: 'var(--mui-common-white)',
            display: "flex",
            fontSize: '0.875rem',
            lineHeight: '1.125rem !important',
            whiteSpace: 'normal',
            '& .MuiTypography-root': {
                fontSize: '0.875rem',
            },
            '&:focus': {
                outline: 'none',
            },
            '&:focus-within': {
                outline: 'none',
            },
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: 'var(--mui-common-white)',
            fontSize: '0.875rem',
        },
        "& .MuiDataGrid-headerFilterRow": {
            "& .MuiInputBase-input": {
                padding: '0.5rem 0.5rem',
            }
        },
        "& .MuiDataGrid-root": {
            border: 'none',
            minHeight: '12.5rem',
            minWidth: '18.75rem',
        },
        "& .MuiDataGrid-virtualScroller": {
            overflow: 'scroll !important'
        },
        "& .MuiDataGrid-row:hover": {
            backgroundColor: 'var(--mui-common-white)',
        },
        "& .MuiTablePagination-displayedRows": {
            fontSize: '0.875rem',
            letterSpacing: '0.01071em',
            lineHeight: 1.43,
        },
        "&&& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: 'var(--mui-common-white)',
        },
        "&&& .MuiDataGrid-row:focus": {
            backgroundColor: 'var(--mui-common-white)',
            outline: 'none',
        },
    },
    dataGridContainer: {
        height: 'auto',
        minWidth: '10.75rem',
        width: '100%',
    },
    avatar: {
        serviceTypeIcon: {
            width: '1.625rem',
            height: '1.625rem',
            backgroundColor: "white"
        }
    },
    chip: {
        amendmentsChip: {
            backgroundColor: orange[50],
            color: orange[900],
            fontWeight: 500,
            borderRadius: '0.8rem',
            border: `0.063rem solid ${orange[200]}`,
            '&:hover': {
                backgroundColor: orange[100],
                borderColor: orange[300],
            },
        }
    },
    icon: {
        image: {
            width: '1.25rem',
            height: '1.25rem',
        }
    },
};

const MyHistoryShiftsTable = ({performerId, paginationModel, onPaginationModelChange}) => {
    const {timezone} = useTimezone();
    const {t} = useTranslation();
    const NO_SHIFT_HISTORY_MESSAGE = t("common.noShiftHistoryMessage");

    const [selectedMonth, setSelectedMonth] = useState(moment().format(YEAR_MONTH_FORMAT));
    const [openAmendmentsDialog, setOpenAmendmentsDialog] = useState(false);
    const [selectedAmendmentRow, setSelectedAmendmentRow] = useState(null);
    const {shifts, amendments, performerActivitySummary, showProgress} = useShiftHistory(selectedMonth, performerId);

    const shiftRows = shifts.map((shift) => {
        const {date, time} = formatDateAndTimeRange(shift.performingFrom, shift.to, timezone);
        const services = Array.from(
            new Set((shift.bookableServiceTypes || [])
                .map((bookableService) => bookableService?.serviceType?.keycode)
                .filter(Boolean)
            )
        );
        const shiftAmendments = amendments.filter((amendment) => amendment?.shift?.id === shift.id);
        const shiftDurationIncludingCheckinInMinutes = calculateShiftDurationIncludingCheckInTimeInMinutes(shift);

        return {
            id: shift.id,
            date,
            time,
            from: shift.from,
            shiftDurationInMinutes: shiftDurationIncludingCheckinInMinutes,
            services,
            amendments: shiftAmendments,
            shiftAmendmentCount: shiftAmendments.length,
        };
    });

    const standaloneAmendmentRows = amendments
        .filter((amendment) => !amendment?.shift?.id)
        .map((amendment) => {
            const amendmentStartTime = amendment?.startTime;
            const amendmentEndTime = moment(amendmentStartTime).add(amendment?.durationInMinutes ?? 0, 'minutes').toISOString();

            const {date, time} = formatDateAndTimeRange(amendmentStartTime, amendmentEndTime, timezone);

            return {
                id: amendment.id,
                date,
                time,
                from: amendmentStartTime,
                shiftDurationInMinutes: 0,
                services: [],
                amendments: [amendment],
                shiftAmendmentCount: 1,
            };
        });

    const rows = [...shiftRows, ...standaloneAmendmentRows]
        .sort((a, b) => (moment(b.from).diff(moment(a.from))));

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
    };

    const handleOpenAmendmentsDialog = (row) => {
        setSelectedAmendmentRow(row);
        setOpenAmendmentsDialog(true);
    };

    const handleCloseAmendmentsDialog = () => {
        setOpenAmendmentsDialog(false);
        setSelectedAmendmentRow(null);
    };

    const totalShiftMinutesIncludingAmendments = (performerActivitySummary?.totalShiftDurationInMinutes ?? 0) + (performerActivitySummary?.netAmendmentMinutes ?? 0);
    const totalShiftsCount = performerActivitySummary?.totalShiftCount ?? shifts.length;
    const totalAmendmentsCount = performerActivitySummary?.totalAmendmentCount ?? amendments.length;
    const totalAmendmentsDeductionCount = amendments.filter((amendment) => amendment?.direction === AMENDMENT_DIRECTION.DEBIT).length;
    const totalAmendmentsAdditionCount = amendments.filter((amendment) => amendment?.direction === AMENDMENT_DIRECTION.CREDIT).length;

    const deductionsLabel = t("common.deductionCountLabel", {count: totalAmendmentsDeductionCount});
    const additionsLabel = t("common.additionCountLabel", {count: totalAmendmentsAdditionCount});
    const amendmentsBreakdownLabel = `${deductionsLabel} · ${additionsLabel}`;

    const monthOptions = Array.from({length: MONTH_OPTIONS_COUNT}, (element, index) => moment().subtract(index, 'months'))
        .filter((month) => !month.isBefore(EARLIEST_MONTH_OPTION, 'month'))
        .map((month) => ({
            value: month.format(YEAR_MONTH_FORMAT),
            label: month.format(MONTH_YEAR_FORMAT),
        }));

    const columns = [
        {
            field: 'date',
            headerName: t("common.shiftTableColumns.date"),
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.date}
                </Typography>
            ),
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'time',
            headerName: t("common.shiftTableColumns.performingTime"),
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.time}
                </Typography>
            ),
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'serviceType',
            headerName: t("common.shiftTableColumns.service"),
            renderCell: (params) => (
                <Box sx={styles.box.serviceRow}>
                    {(params.row.services || []).map((service) => (
                        <Tooltip key={service} title={service}>
                            <Avatar sx={styles.avatar.serviceTypeIcon}>
                                <Box
                                    component="img"
                                    src={retrieveServiceTypeIcon([service])}
                                    sx={styles.icon.image}/>
                            </Avatar>
                        </Tooltip>
                    ))}
                </Box>
            ),
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'duration',
            headerName: t("common.shiftTableColumns.totalHours"),
            renderHeader: () => (
                <Tooltip title={t("common.shiftTableColumns.totalHoursTooltip")}>
                        {t("common.shiftTableColumns.totalHours")}
                </Tooltip>
            ),
            renderCell: (params) => {
                const shiftDurationInMinutes = params.row.shiftDurationInMinutes ?? 0;
                const amendmentsDurationInMinutes = calculateNetAmendmentDurationInMinutes(params.row.amendments);
                const durationLabel = formatMinutesAsHours(shiftDurationInMinutes + amendmentsDurationInMinutes);

                return (
                    <Typography variant="body2">
                        {durationLabel}
                    </Typography>
                );
            },
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'amendments',
            headerName: t("common.shiftTableColumns.amendments"),
            renderCell: (params) => (params.row.shiftAmendmentCount > 0
                &&
                <Chip
                    onClick={() => handleOpenAmendmentsDialog(params.row)}
                    sx={styles.chip.amendmentsChip}
                    clickable
                    label={t("common.amendmentCountLabel", {count: params.row.shiftAmendmentCount})}/>
            ),
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'notes',
            headerName: t("common.shiftTableColumns.notes"),
            renderCell: (params) => {
                const notes = params.row.amendments
                    ?.map(amendment => amendment.notes)
                    .filter(Boolean)
                    .join(', ');

                return (
                    <Tooltip title={notes || ''}>
                        <Typography variant="body2" noWrap>
                            {notes}
                        </Typography>
                    </Tooltip>
                );
            },
            width: 350,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },

    ]

    return (
        <>
            <MyHistoryShiftsSummaryTab
                selectedMonth={selectedMonth}
                monthOptions={monthOptions}
                handleMonthChange={handleMonthChange}
                totalShiftsCount={totalShiftsCount}
                totalShiftMinutesIncludingAmendments={totalShiftMinutesIncludingAmendments}
                totalAmendmentsCount={totalAmendmentsCount}
                amendmentsBreakdownLabel={amendmentsBreakdownLabel}/>
            <Box sx={styles.box.gridContainer}>
                <Box sx={styles.box.progressTrack}>
                    {
                      showProgress
                      &&
                      <>
                          <Backdrop sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}} open={showProgress}>
                              <CircularProgress color="inherit"/>
                          </Backdrop>
                      </>
                    }
                </Box>
                <DataGridPro
                    columns={columns}
                    disableRowSelectionOnClick
                    rowSelection={false}
                    disableColumnPinning
                    disableColumnSelector
                    disableColumnReorder
                    getRowHeight={() => SINGLE_LINE_ROW_HEIGHT_PIXELS}
                    rows={rows}
                    pagination
                    rowCount={rows.length}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChange}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    slotProps={{
                        columnsPanel: {
                            disableHideAllButton: true,
                            disableShowAllButton: true,
                        },
                        headerFilterCell: {
                            showClearIcon: true,
                        }
                    }}
                    slots={{
                        noRowsOverlay: () => <NoRowsOverlayLabel message={NO_SHIFT_HISTORY_MESSAGE} />
                    }}
                    sx={[styles.dataGrid, styles.dataGridContainer]}
                />
            </Box>
            <AmendmentDetailsDialog
                open={openAmendmentsDialog}
                onClose={handleCloseAmendmentsDialog}
                selectedAmendmentRow={selectedAmendmentRow}
                timezone={timezone}/>
        </>
    )
}

export default MyHistoryShiftsTable;